import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Layout from "@/components/layout/Layout";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { money } from "@/lib/signs";
import { captureProof } from "@/lib/proof";
import DesignerBar from "@/components/signs/DesignerBar";
import ApparelCanvas, { VIEW_LABELS } from "@/components/shirts/ApparelCanvas";
import ArtRail from "@/components/shirts/ArtRail";
import ShirtDock from "@/components/shirts/ShirtDock";
import { useShirtConfig } from "@/components/shirts/useShirtConfig";
import { useMall } from "@/context/MallContext";
import { cardTheme } from "@/lib/mall";

const STAGE_BG = "#e3e6ea";
const STAGE = { background: "radial-gradient(circle at 50% 40%, #f6f7f9 0%, #e3e6ea 55%, #cfd4da 100%)" };

/** Front / back / sleeve switch that floats over the garment. A dot marks the sides that already carry art or text. */
function ViewTabs({ cfg, theme }) {
    const has = (v) => cfg.arts[v]?.file || (cfg.texts[v] || []).some((t) => t.text?.trim());
    return (
        <div className="absolute left-4 top-4 z-10 flex rounded-full border border-zinc-200 bg-white p-0.5 shadow-sm" data-testid="view-tabs">
            {cfg.views.map((v) => (
                <button key={v} type="button" onClick={() => cfg.setView(v)} data-testid={`view-tab-${v}`}
                    className="relative rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider transition-colors"
                    style={cfg.view === v ? { background: theme.primary, color: theme.on } : { color: "#71717a" }}>
                    {VIEW_LABELS[v]}
                    {has(v) && <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full" style={{ background: cfg.view === v ? theme.on : theme.primary }} data-testid={`view-dot-${v}`} />}
                </button>
            ))}
        </div>
    );
}

/** The apparel designer: one bar, images on the left, the garment in the middle, options along the bottom. Fits one screen. */
export default function ShirtProduct() {
    const { kind, slug } = useParams();
    const { addItem, setOpen } = useCart();
    const { store: activeStore, ensureStore, enter } = useMall();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [selText, setSelText] = useState(null);
    const stageRef = useRef(null);

    useEffect(() => { ensureStore(); }, [ensureStore]);

    useEffect(() => {
        setLoading(true);
        api.get(`/shirts/products/${slug}`).then(({ data }) => setData(data))
            .catch(() => toast.error("That product could not be found"))
            .finally(() => setLoading(false));
    }, [slug]);

    const product = data?.product;
    const productStore = product?.store;
    useEffect(() => {
        if (!activeStore && productStore?.slug) enter(productStore, "product");
    }, [activeStore, productStore, enter]);
    const cfg = useShirtConfig(product);

    const add = async () => {
        setAdding(true);
        try {
            const proof = await captureProof(stageRef.current, `${product.slug}-${cfg.view}`, STAGE_BG);
            const q = cfg.quote;
            addItem({
                product_slug: product.slug,
                product_name: `${product.name}${cfg.color?.name ? ` — ${cfg.color.name}` : ""}`,
                image: proof ? mediaUrl(proof.url) : mediaUrl((product.mockups?.front || {}).url),
                quantity: q.quantity,
                unit_price: q.unit_price,
                line_total: q.line_total,
                summary: [`${q.quantity} pcs`, cfg.color?.name, q.locations.join(" + "),
                    q.decoration?.stitch_label, q.decoration?.screens ? `${q.decoration.screens} screens` : null,
                    q.setup_fee ? `${money(q.setup_fee)} setup` : null,
                ...q.lines.map((l) => `${l.label} ×${l.quantity}`)].filter(Boolean).join(" · "),
                store: product.store,
                selections: {
                    kind: "shirt",
                    category: product.category,
                    store_slug: product.store?.slug || product.store_slug,
                    turnaround: product.turnaround,
                    color: cfg.color,
                    sizes: cfg.sizes,
                    locations: cfg.locations,
                    decoration: product.decoration ? cfg.deco : undefined,
                    art: Object.fromEntries(cfg.locations.filter((v) => cfg.arts[v]?.file).map((v) => [v, {
                        file: cfg.arts[v].file, width_in: cfg.arts[v].widthIn,
                        height_in: Number((cfg.arts[v].widthIn / cfg.arts[v].ratio).toFixed(2)),
                        offset: { x: cfg.arts[v].x, y: cfg.arts[v].y },
                    }])),
                    texts: Object.fromEntries(Object.entries(cfg.texts).filter(([, l]) => (l || []).length)),
                },
                proof,
                artwork: cfg.locations.map((v) => cfg.arts[v]?.file).filter(Boolean),
            });
            toast.success("Added to cart");
            setOpen(true);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setAdding(false);
        }
    };

    if (loading) return <Layout designer><div className="flex min-h-[60vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div></Layout>;
    if (!product) {
        return (
            <Layout designer>
                <div className="mx-auto max-w-3xl px-4 py-24 text-center">
                    <h1 className="font-display text-3xl font-black">Product not found</h1>
                    <Link to="/shirts" className="mt-4 inline-block underline">Back to Shirts</Link>
                </div>
            </Layout>
        );
    }

    const theme = cardTheme({ store: product.store }, activeStore);
    const q = cfg.quote;
    const errors = q?.errors || [];
    // No sizes picked yet means no quote — the cart has nothing to take, so say so instead of failing quietly.
    const noQuote = !q?.quantity;
    const priceNote = q?.unit_price
        ? [`${q.quantity} pcs`, `${money(q.unit_price)} each`, q.locations?.length ? q.locations.map((v) => VIEW_LABELS[v] || v).join(" + ") : "no print", q.setup_fee ? `+ ${money(q.setup_fee)} setup` : null].filter(Boolean).join(" · ")
        : "Pick your sizes to price this up";
    const perLocation = product.pricing?.per_location_fee ? `${money(product.pricing.per_location_fee)} each` : "";

    return (
        <Layout designer>
            <div className="flex flex-col bg-white lg:h-[calc(100vh-105px)] lg:overflow-hidden" data-testid="shirt-designer" data-kind={kind || product.category}>
                <DesignerBar back="/shirts" backLabel="Shirts" backTestid="back-to-kind" prefix="shirt"
                    title={product.name} description={[product.brand, `from ${money(product.pricing?.base_per_piece)}/pc`].filter(Boolean).join(" · ")}
                    store={product.store} turnaround={product.turnaround} theme={theme}
                    price={q?.line_total} priceNote={priceNote} errors={[...errors, !errors.length && noQuote && !cfg.pricing ? "Pick your sizes" : null]} sale={q?.sale}
                    busy={cfg.pricing} blocked={cfg.pricing || errors.length > 0 || noQuote || adding} onAdd={add} adding={adding} />

                <div className="grid min-h-0 flex-1 lg:grid-cols-[280px_1fr]">
                    <ArtRail product={product} cfg={cfg} theme={theme} perLocation={perLocation} className="order-2 border-t border-zinc-200 lg:order-1 lg:border-r lg:border-t-0" />
                    <div ref={stageRef} className="relative order-1 h-[52vh] min-h-[360px] lg:order-2 lg:h-auto lg:min-h-0" style={STAGE} data-testid="shirt-stage">
                        <ApparelCanvas product={product} view={cfg.view} art={cfg.arts[cfg.view]}
                            onArtChange={(next) => cfg.setArt(cfg.view, next)} color={cfg.color}
                            texts={cfg.viewTexts} onTextChange={cfg.setText} selectedText={selText} onSelectText={setSelText} />
                        <ViewTabs cfg={cfg} theme={theme} />
                        <span className="absolute right-4 top-4 z-10 rounded-full bg-white/90 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 shadow-sm" data-testid="stage-caption">
                            {[cfg.color?.name, product.turnaround].filter(Boolean).join(" · ")}
                        </span>
                    </div>
                </div>

                <ShirtDock product={product} cfg={cfg} theme={theme} selText={selText} setSelText={setSelText} />
            </div>
        </Layout>
    );
}
